import { Box } from '@mui/material';
import { Handle, Position } from '@xyflow/react';
import { useTranslate } from 'react-admin';

import { translateCallProperty } from '../../components/CallPropertyInput';
import NodeIcon from '../../components/NodeIcon';
import NodeBody from '../NodeBody';
import NodeHeader from '../NodeHeader';
import NodeWrapper from '../NodeWrapper';

import type { MappingType } from './MappingControls';
import type { NodeProps } from '@xyflow/react';

/**
 * Renders a MappingNode component.
 *
 * @param {NodeProps<MappingType>} data - The data to be passed to the component.
 * @return {React.ReactElement} The rendered MappingNode component.
 */
export default function MappingNode({ data }: NodeProps & { data: MappingType }): React.ReactElement {
  const translate = useTranslate();
  const mappingRules = data.mappingRules ?? [];
  const isInvalid =
    mappingRules.length === 0 ||
    mappingRules.some((mappingRule) => mappingRule.matchRule === '' || mappingRule.replaceRule === '');

  const errors = isInvalid ? ['callflows.nodes.mapping.mappingRuleErrorEmpty'] : [];
  const description = [
    typeof data.matchProperty === 'string' && data.matchProperty.length > 0
      ? `${translate('callflows.nodes.router.matchProperty')}: ${translateCallProperty(translate, data.matchProperty)}`
      : undefined,
    typeof data.setProperty === 'string' && data.setProperty.length > 0
      ? `${translate('callflows.nodes.mapping.setProperty')}: ${translateCallProperty(translate, data.setProperty)}`
      : undefined,
  ]
    .filter((value): value is string => value !== undefined)
    .join('\n');

  return (
    <NodeWrapper errors={errors}>
      <NodeHeader
        icon={<NodeIcon name='mapping' />}
        title={translate('callflows.nodes.mapping.name')}
        description={description}
      />
      <Handle type='target' position={Position.Left} id='mapping-target' />
      <Handle type='source' position={Position.Right} id='mapping-source' />
      {mappingRules.length > 0 && (
        <NodeBody type='ivr' backgroundColor='transparent'>
          {mappingRules.map((mappingRule, index) => (
            <Box
              // Can be disabled as we are not reordering mappingrules:
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              data-testid='mapping-rule'
              sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
            >
              {translate('callflows.nodes.mapping.mapTo', {
                matchRule: mappingRule.matchRule,
                replaceRule: mappingRule.replaceRule,
              })}
            </Box>
          ))}
        </NodeBody>
      )}
    </NodeWrapper>
  );
}
